/**
 * The four survey emails, and what they say.
 *
 * Content lives here; the schedule lives in FollowUpSettings below. That split is
 * deliberate — the card below already owns one dirty-gated Save over three numeric
 * fields, and hanging modal-based content editing off the same card would put two
 * different save semantics in one box, which is the reliable way to make an admin
 * lose work.
 *
 * So timing is READ-ONLY here and read from the same settings endpoint the card
 * below writes. One source of truth, nothing to drift.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import apiService from '../../services/api';
import { usePermissionAccess } from '../auth/RoleGuard';
import SurveyEmailEditModal from './SurveyEmailEditModal';
import { SurveyEmail, SurveyEmailsResponse } from './types';

const TEAL = '#1A5F7A';

interface Props {
  /** Bumped by the parent after a sync, so the card re-reads. */
  refreshKey?: number;
  /** Which survey's emails to show. Null while the switcher is still loading. */
  campaign?: string | null;
}

/**
 * Timing is expressed in the same words the follow-up card uses, and the two emails
 * that are not on a schedule say what actually triggers them rather than being left
 * blank — "no timing" reads as broken.
 */
const describeTiming = (
  email: SurveyEmail,
  settings: { followup1_hours?: number; followup2_hours?: number } | null
): string => {
  if (email.timing === 'manual') return 'Sent when you press “Send invitations”';
  if (email.timing === 'on_completion') return 'Sent automatically when a member completes the survey';
  const hours =
    email.timing === 'followup1_hours' ? settings?.followup1_hours : settings?.followup2_hours;
  if (hours == null) return 'Sent after the invite';
  const days = hours / 24;
  const dayText = Number.isInteger(days) ? days : days.toFixed(1);
  return `Sent ${hours} hours after the invite — ${dayText} day${days === 1 ? '' : 's'}`;
};

const SurveyEmailsCard: React.FC<Props> = ({ refreshKey = 0, campaign = null }) => {
  const { hasFullAccess } = usePermissionAccess();
  // A view_only admin can open this page, so without this they would fill in the
  // modal and then eat a 403 on Save. Same precedent as FollowUpSettings.
  const canEdit = hasFullAccess('audience_manager');

  const [data, setData] = useState<SurveyEmailsResponse | null>(null);
  const [settings, setSettings] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [editing, setEditing] = useState<SurveyEmail | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Settings is allowed to fail on its own: without it the timing lines degrade
      // to "Sent after the invite" rather than taking the whole card down.
      const [emailsRes, settingsRes] = await Promise.all([
        apiService.getSurveyEmails(campaign || undefined),
        apiService.getSurveySettings(campaign || undefined).catch(() => null),
      ]);
      setData(emailsRes);
      setSettings(settingsRes?.settings ?? null);
    } catch (e: any) {
      setError(
        e?.message === 'Unauthorized'
          ? 'Your session expired. Reload the page to sign in again.'
          : e?.message || 'Could not load the survey emails.'
      );
      setData(null);
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    // Waits for the switcher: a request with no campaign would show the live
    // survey's emails for a beat and then swap them.
    if (campaign) load();
  }, [load, refreshKey, campaign]);

  const followUpsOff = settings ? !settings.enabled : false;

  const lastEdited = useMemo(() => {
    if (!data?.updated_at) return null;
    const when = new Date(data.updated_at);
    if (Number.isNaN(when.getTime())) return null;
    return `Last edited ${when.toLocaleString()}${data.updated_by ? ` by ${data.updated_by}` : ''}`;
  }, [data]);

  const handleSaved = (res: SurveyEmailsResponse) => {
    setData(res);
    setEditing(null);
    setNote('Saved. The next send will use the new wording.');
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography
          sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827' }}
        >
          Survey emails
        </Typography>
        {data && !data.seeded && (
          <Chip
            size="small"
            label="Defaults"
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#FEF3C7', color: '#92400E' }}
          />
        )}
      </Box>

      <Typography sx={{ fontSize: '12px', color: '#6B7280', mb: 2 }}>
        What each of the four emails says. Send timing is set in <strong>Follow-up emails</strong>{' '}
        below.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
          {error}
        </Alert>
      )}
      {note && (
        <Alert severity="success" onClose={() => setNote(null)} sx={{ mb: 2, fontSize: 13 }}>
          {note}
        </Alert>
      )}

      {data && !data.seeded && !loading && (
        <Alert severity="info" icon={false} sx={{ mb: 2, fontSize: 13, bgcolor: '#FFFBEB' }}>
          These are the copies that shipped with the current deployment. Saving any of them
          stores an editable version — nothing about what members receive changes until you do.
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
      ) : (
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
          {(data?.emails || []).map((email, i) => {
            // An admin editing copy for an email that is switched off, with nothing
            // saying so, is a trap. The follow-ups are the only two the switch governs.
            const paused = followUpsOff && email.timing.startsWith('followup');
            return (
              <Box
                key={email.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.75,
                  borderTop: i === 0 ? 'none' : '1px solid #F3F4F6',
                  bgcolor: paused ? '#FAFAFA' : 'transparent',
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 700, color: paused ? '#6B7280' : '#111827' }}
                    >
                      {email.label}
                    </Typography>
                    {paused && (
                      <Chip
                        size="small"
                        label="Paused"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: '#F3F4F6',
                          color: '#6B7280',
                        }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: '#374151', mb: 0.25 }}>
                    {email.values.subject || <em>No subject set</em>}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                    {describeTiming(email, settings)}
                    {paused && ' — but follow-ups are switched off, so this will not send.'}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setEditing(email)}
                  disabled={!canEdit}
                  sx={{
                    textTransform: 'none',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    borderColor: TEAL,
                    color: TEAL,
                  }}
                >
                  Edit
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        {!canEdit && (
          <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
            You have view-only access to this area.
          </Typography>
        )}
        {canEdit && lastEdited && (
          <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>{lastEdited}</Typography>
        )}
      </Box>

      {editing && data && (
        <SurveyEmailEditModal
          email={editing}
          rev={data.rev}
          campaign={campaign}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </Paper>
  );
};

export default SurveyEmailsCard;
